import immutableStore from 'alt-utils/lib/ImmutableUtil';
import immutable from 'immutable';
import alt from 'components/Dispatcher';
import CrmSyncActions from 'actions/crm/CrmSyncActions';
import CrmSyncContactsAndLeadsPrefixSearchSource from 'sources/crm/CrmSyncContactsAndLeadsPrefixSearchSource';
import CrmSyncContactsByAccountIdSource from 'sources/crm/CrmSyncContactsByAccountIdSource';
import makePromiseCancelable from './makePromiseCancelable';

/**
 * Store the state of <CallSyncCRMContactLeadSearchInputBox />
 */
@immutableStore
class CrmSyncContactsAndLeadsPrefixSearchStore {
  constructor() {
    this.state = immutable.Map({
      // options store the list of possible contacts/leads for the current search term
      options: immutable.List(),
      // total stores the number of total contacts/leads for the current search term since we show only a part of the
      // total instead of all of them
      total: 0,
      // numReturned stores the number of contacts/leads returned for the current search term. This value is used in
      // combination with `total` to show how many contacts/leads remain to be returned and we can ask the user to type
      // more in order to narrow down the search results
      numReturned: 0,
      // values store the list of current selected values for contacts-and-leads
      values: immutable.List(),
      // loading is a boolean that indicates whether there is a request running or not. This value is used for queueing
      // search requests
      loading: false,
      // searchPrefixPromise stores the current request that searches the backend for contacts/leads. This request is
      // cancellable
      searchPrefixPromise: null,
      searchPrefixPromiseIsCanceled: false,
    });
    this.bindActions(CrmSyncActions);
    this.registerAsync(CrmSyncContactsAndLeadsPrefixSearchSource);
    this.registerAsync(CrmSyncContactsByAccountIdSource);
  }

  /** Clear the "Contacts-and-Leads" options */
  onClearContactsAndLeadsOptions() {
    const state = this.state.set('options', immutable.List());
    this.setState(state);
  }

  onCancelPrefixSearchContactsAndLeads() {
    let state = this.state.set('loading', false);
    const promise = this.state.get('searchPrefixPromise');
    if (promise) {
      promise.cancel();
      state = this.state.set('searchPrefixPromiseIsCanceled', true);
    }
    this.setState(state);
  }

  /**
   * Update this.state.get('values') with a new array of values
   * @param {Array.<String>} values User selected contacts / leads values
   */
  onUpdateCurrentPrefixSearchContactsAndLeadsValues(values) {
    const state = this.state.set('values', immutable.List(values));
    this.setState(state);
  }

  /** Clear this.state.get('values') and set it back to immutable.List() */
  onClearCurrentPrefixSearchContactsAndLeadsValues() {
    const state = this.state.set('values', immutable.List());
    this.setState(state);
  }

  /**
   * Trigger a request to the backend to search for contacts/leads with the given term as their prefix
   * @param term {string} Current search term as the prefix
   */
  onPrefixSearchContactsAndLeads(args) {
    const [term, contactAccountId] = args;
    setImmediate(() => {
      const searchPrefixReq = makePromiseCancelable(
        this.getInstance().searchPrefix(term, contactAccountId)
      );
      searchPrefixReq.promise
        .then(() => console.log('Resolved onPrefixSearchContactsAndLeads'))
        .catch(({isCanceled, ...error}) => {
          console.log('onPrefixSearchContactsAndLeads isCanceled', isCanceled);
        });
      const state = this.state.set('searchPrefixPromise', searchPrefixReq);
      this.setState(state);
    });
  }

  /** Update this.state.get('loading') */
  onPrefixSearchContactsAndLeadsLoading() {
    const state = this.state.set('loading', true);
    this.setState(state);
  }

  /**
   * Populate this.state.get('options') with server-side response that includes the contacts/leads for a given prefix
   * @param resp {Object} Server-side response that includes contacts/leads related data
   */
  onPrefixSearchContactsAndLeadsSuccess(resp) {
    let state = this.state.set('loading', false);
    if (!(state.get('searchPrefixPromise') && state.get('searchPrefixPromiseIsCanceled'))) {
      const data = resp.data;
      state = state.set('options', immutable.List(data.contacts_and_leads));
      state = state.set('numReturned', data.num_returned);
      state = state.set('total', data.total);
    }
    state = state.set('searchPrefixPromise', null);
    state = state.set('searchPrefixPromiseIsCanceled', false);
    this.setState(state);
  }

  /** Update this.state.get('loading')
   * @param resp {Object} Server-side response that includes an error response
   */
  onPrefixSearchContactsAndLeadsError(resp) {
    let state = this.state.set('loading', false);
    this.setState(state);
  }

  onFindContactsByAccountId(accountId) {
    setImmediate(() => {
      this.getInstance().findContactsByAccountId(accountId);
    });
  }

  onFindContactsByAccountIdLoading() {
    const state = this.state.set('loading', true);
    this.setState(state);
  }

  onFindContactsByAccountIdSuccess(resp) {
    const data = resp.data;
    const options = immutable.List(data.contacts);
    let state = this.state.set('options', options);
    state = state.set('numReturned', options.count());
    state = state.set('total', options.count());
    state = state.set('loading', false);
    this.setState(state);
  }

  onFindContactsByAccountIdError(resp) {
    let state = this.state.set('loading', false);
    state = state.set('searchPrefixPromise', null);
    state = state.set('searchPrefixPromiseIsCanceled', false);
    this.setState(state);
  }
}

export default alt.createStore(
  CrmSyncContactsAndLeadsPrefixSearchStore,
  'CrmSyncContactsAndLeadsPrefixSearchStore'
);
